'use client';

import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { cn } from '@savvy-kit/lib/utils';
import { cva } from 'class-variance-authority';
import { XIcon } from 'lucide-react';

import { Accordion } from '../accordion/accordion';

export type SheetPortalContainer = NonNullable<DialogPrimitive.Portal.Props['container']>;

function Sheet(props: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger(props: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetPortal(props: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetClose(props: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/20 duration-300 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

const sheetContentVariants = cva(
  'bg-background-light fixed inset-y-0 z-50 flex h-full flex-col overflow-hidden border border-light shadow-lg outline-none duration-300 data-open:animate-in data-closed:animate-out',
  {
    variants: {
      side: {
        right: 'right-0 rounded-l-lg data-open:slide-in-from-right data-closed:slide-out-to-right',
        left: 'left-0 rounded-r-lg data-open:slide-in-from-left data-closed:slide-out-to-left',
      },
      size: {
        sm: 'w-full sm:max-w-sm',
        md: 'w-full sm:max-w-md',
        lg: 'w-full sm:max-w-lg',
        xl: 'w-full sm:max-w-xl',
      },
    },
    defaultVariants: {
      side: 'right',
      size: 'md',
    },
  },
);

type SheetContentProps = DialogPrimitive.Popup.Props &
  VariantProps<typeof sheetContentVariants> & {
    toolbar?: React.ReactNode;
    portalContainer?: SheetPortalContainer;
    overlayClassName?: string;
  };

function SheetContent({
  className,
  children,
  side,
  size,
  toolbar,
  portalContainer,
  overlayClassName,
  ...props
}: SheetContentProps) {
  return (
    <SheetPortal container={portalContainer}>
      <SheetOverlay className={overlayClassName} />
      <DialogPrimitive.Popup
        data-slot="sheet-content"
        className={cn(sheetContentVariants({ side, size }), className)}
        {...props}
      >
        <div
          data-slot="sheet-toolbar"
          className="bg-card flex items-center justify-between gap-2 border-b border-light px-3 py-2"
        >
          <div className="flex items-center gap-1">{toolbar}</div>
          <DialogPrimitive.Close
            data-slot="sheet-close"
            aria-label="Close"
            className="ring-offset-background focus:ring-ring rounded-md p-1.5 text-secondary opacity-80 transition-opacity hover:bg-component-hover hover:text-dark hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 hover:cursor-pointer"
          >
            <XIcon />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('bg-card flex flex-col gap-1.5 border-b border-light px-6 py-5', className)}
      {...props}
    />
  );
}

function SheetTabs({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-tabs" className={cn('bg-card border-b border-light px-6 py-3', className)} {...props} />;
}

// Accordion region below the header — the accordion counterpart to SheetTabs, for
// stepped/collapsible flows. Wraps the kit Accordion so callers compose AccordionItem
// children and drive open state via `value` / `onValueChange`.
function SheetAccordion({ className, ...props }: React.ComponentProps<typeof Accordion>) {
  return (
    <div data-slot="sheet-accordion" className="bg-card border-b border-light">
      <Accordion className={cn('px-6', className)} {...props} />
    </div>
  );
}

function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-body" className={cn('flex-1 overflow-auto px-6 py-5', className)} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        'bg-card flex flex-col-reverse gap-2 border-t border-light px-6 py-4 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-secondary text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetAccordion,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTabs,
  SheetTitle,
  SheetTrigger,
};
